import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { errorToastFromAxios } from "../../lib/supportToast";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Landmark, RefreshCw, AlertTriangle, CheckCircle2, XCircle,
  Webhook, Users, ArrowLeftRight, Ban, Info, Download,
} from "lucide-react";

const TABS = [
  ["overview", "Overview"],
  ["businesses", "Businesses"],
  ["accounts", "Accounts"],
  ["persons", "People"],
  ["limits", "Limit requests"],
  ["fees", "Verqo fees"],
  ["treasury", "Verqo account"],
  ["transfers", "Transfers"],
  ["deficits", "Exposure"],
  ["events", "Webhooks"],
  ["drift", "Drift"],
];

const DRIFT_LABEL = {
  match: ["Agrees", "text-emerald-700", "Verqo and Shield show the same spendable figure"],
  verqo_ahead: ["Verqo ahead", "text-red-600", "Verqo shows money Shield does not hold"],
  shield_ahead: ["Shield ahead", "text-amber-600", "Shield holds money Verqo does not show"],
  no_shield_business: ["No Shield business", "text-foreground/50", "Never provisioned at Shield"],
  not_mirrored: ["Not mirrored", "text-red-600", "Has a balance in Verqo with no Shield business behind it"],
  shield_unreachable: ["Unreachable", "text-foreground/50", "Shield did not answer for this business"],
};

// Always to the cent — money() renders $53.5 which looks broken on a ledger.
const usd = (cents) =>
  ((cents || 0) / 100).toLocaleString("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const Stat = ({ label, value, tone = "", testId, hint }) => (
  <div className="rounded-xl border border-foreground/10 bg-background p-4" data-testid={testId}>
    <div className="text-xs uppercase tracking-wide text-foreground/50">{label}</div>
    <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone}`}>{value}</div>
    {hint ? <div className="mt-1 text-xs text-foreground/45">{hint}</div> : null}
  </div>
);

export default function AdminShield() {
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [deficits, setDeficits] = useState(null);
  const [events, setEvents] = useState(null);
  const [drift, setDrift] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [persons, setPersons] = useState(null);
  const [limits, setLimits] = useState(null);
  const [fees, setFees] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const [bankForm, setBankForm] = useState({ routing_number: "", account_number: "", bank_name: "", nickname: "Verqo operating account" });
  const [payoutForm, setPayoutForm] = useState({ amount: "", rail: "ach", memo: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/shield/overview");
      setOv(r.data);
    } catch (e) {
      errorToastFromAxios(e, "Could not load the Shield overview.");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        if (tab === "businesses") setBusinesses((await api.get("/admin/shield/businesses")).data.businesses || []);
        if (tab === "transfers") setTransfers((await api.get("/admin/shield/transfers")).data.transfers || []);
        if (tab === "deficits") setDeficits((await api.get("/admin/shield/deficits")).data);
        if (tab === "events") setEvents((await api.get("/admin/shield/events")).data);
        if (tab === "drift") setDrift((await api.get("/admin/drift/report?limit=5000")).data);
        if (tab === "accounts") setAccounts((await api.get("/admin/shield/accounts")).data);
        if (tab === "persons") setPersons((await api.get("/admin/shield/persons")).data);
        if (tab === "limits") setLimits((await api.get("/admin/shield/limit-requests?status=all")).data);
        if (tab === "fees") setFees((await api.get("/admin/shield/fees")).data);
        if (tab === "treasury") setTreasury((await api.get("/admin/shield/treasury")).data);
      } catch (e) {
        errorToastFromAxios(e, "Could not load that tab.");
      }
    })();
  }, [tab]);

  const trigger = async (kind, id, outcome) => {
    try {
      await api.post(`/admin/shield/test/${kind}/${id}/${outcome}`);
      toast.success(`${kind.slice(0, -1)} → ${outcome}`);
      if (kind === "persons") {
        setPersons((await api.get("/admin/shield/persons")).data);
      } else if (kind === "businesses") {
        setBusinesses((await api.get("/admin/shield/businesses")).data.businesses || []);
        setAccounts((await api.get("/admin/shield/accounts")).data);
      } else {
        setTransfers((await api.get("/admin/shield/transfers")).data.transfers || []);
      }
      load();
    } catch (e) {
      errorToastFromAxios(e, "Trigger refused.");
    }
  };

  const verifyPlatform = async () => {
    try {
      const r = await api.post("/admin/shield/platform/onboarding-link", {
        tax_id: "000000000",
      });
      if (r.data?.link?.url) {
        window.open(r.data.link.url, "_blank", "noopener");
        toast.info("Finish the form in the new tab, then refresh this page.");
      } else {
        toast.success("Verqo's account is already open.");
        setAccounts((await api.get("/admin/shield/accounts")).data);
      }
    } catch (e) {
      errorToastFromAxios(e, "Could not start platform verification.");
    }
  };

  const decideLimit = async (req, decision) => {
    const asked = req.requested_daily_limit_cents / 100;
    let granted = asked;
    if (decision === "approve") {
      const typed = window.prompt(
        `Approve a daily limit for ${req.company || req.email}.\nThey asked for $${asked.toLocaleString()}.`,
        String(asked),
      );
      if (typed === null) return;
      granted = Number(typed);
      if (!granted || granted <= 0) return toast.error("Enter a positive figure.");
    }
    const note = window.prompt(
      decision === "approve"
        ? "Note for the member (optional)."
        : "Why is this declined? The member sees this.",
      "",
    );
    if (decision === "decline" && !note) return toast.error("A reason is required.");
    try {
      await api.post(`/admin/shield/limit-requests/${req.id}/decide`, {
        decision, granted_daily_limit: granted, note: note || "",
      });
      toast.success(decision === "approve" ? "Limit raised." : "Request declined.");
      setLimits((await api.get("/admin/shield/limit-requests?status=all")).data);
    } catch (e) {
      errorToastFromAxios(e, "That decision did not save.");
    }
  };

  const retryFee = async (sweepId) => {
    try {
      await api.post(`/admin/shield/fees/${sweepId}/retry`);
      toast.success("Fee sweep retried.");
      setFees((await api.get("/admin/shield/fees")).data);
    } catch (e) {
      errorToastFromAxios(e, "That retry did not go through.");
    }
  };

  const receive = async (accountId) => {
    try {
      await api.post(`/admin/shield/test/accounts/${accountId}/receive`, {
        amount: 250, rail: "ach", originator_name: "Outside Payer LLC",
        description: "Admin console test inbound",
      });
      toast.success("Inbound payment simulated.");
      setAccounts((await api.get("/admin/shield/accounts")).data);
      load();
    } catch (e) {
      errorToastFromAxios(e, "That inbound payment was refused.");
    }
  };

  const resolveDeficit = async (transferId) => {    try {
      await api.post(`/admin/shield/deficits/${transferId}/resolve`, {
        note: "Marked repaid from the admin console.",
      });
      toast.success("Marked as repaid.");
      setDeficits((await api.get("/admin/shield/deficits")).data);
      load();
    } catch (e) {
      errorToastFromAxios(e, "Could not update that exposure.");
    }
  };

  const coverDeficit = async (transferId) => {
    try {
      await api.post(`/admin/shield/deficits/${transferId}/cover`, {});
      toast.success("Covered from Verqo's account.");
      setDeficits((await api.get("/admin/shield/deficits")).data);
      load();
    } catch (e) {
      errorToastFromAxios(e, "Could not cover that deficit.");
    }
  };

  const reloadTreasury = async () =>
    setTreasury((await api.get("/admin/shield/treasury")).data);

  const saveTreasuryBank = async () => {
    setBusy(true);
    try {
      await api.post("/admin/shield/treasury/bank", bankForm);
      toast.success("Verqo's bank saved.");
      setBankForm({ ...bankForm, routing_number: "", account_number: "" });
      await reloadTreasury();
    } catch (e) {
      errorToastFromAxios(e, "Could not save that bank.");
    } finally { setBusy(false); }
  };

  const removeTreasuryBank = async () => {
    try {
      await api.delete("/admin/shield/treasury/bank");
      toast.success("Bank removed.");
      await reloadTreasury();
    } catch (e) {
      errorToastFromAxios(e, "Could not remove that bank.");
    }
  };

  const sendTreasuryPayout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/shield/treasury/payout", {
        amount: Number(payoutForm.amount),
        rail: payoutForm.rail,
        memo: payoutForm.memo,
      });
      toast.success(r.data?.instant
        ? `Sent instantly to ${r.data.to}.`
        : `On its way to ${r.data.to}.`);
      setPayoutForm({ ...payoutForm, amount: "", memo: "" });
      await reloadTreasury();
      load();
    } catch (e) {
      errorToastFromAxios(e, "That withdrawal did not go through.");
    } finally { setBusy(false); }
  };

  const downloadDrift = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/drift/report.csv?limit=5000", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `verqo-shield-drift-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      errorToastFromAxios(e, "Could not build the drift export.");
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {    setBusy(true);
    try {
      const r = await api.post("/admin/shield/sync");
      toast.success(`Re-synced ${r.data.transfers_synced} transfers from Shield.`);
      load();
    } catch (e) {
      errorToastFromAxios(e, "Sync failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-shield-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Landmark className="h-6 w-6 text-foreground/50" />
          <div>
            <h1 className="text-2xl font-semibold">Banking rail</h1>
            <p className="text-sm text-foreground/60">
              Businesses, money movement, escrow and exposure across the platform.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sync} disabled={busy} data-testid="admin-shield-sync-btn">
            <ArrowLeftRight className="mr-2 h-4 w-4" /> Re-sync
          </Button>
          <Button variant="outline" onClick={load} disabled={busy} data-testid="admin-shield-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {ov?.env && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
             data-testid="admin-shield-mode-banner">
          <Info className="h-4 w-4" />
          <span className="font-medium">{ov.env.livemode ? "LIVE MODE" : "TEST MODE"}</span>
          <span className="text-amber-800/80">
            {ov.env.livemode
              ? "Real money moves on this key."
              : "No real money moves. Test triggers stand in for the bank and the carrier."}
          </span>
          <span className="ml-auto font-mono text-xs">{ov.account?.name} · {ov.account?.id}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-1 border-b border-foreground/10">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
                  data-testid={`admin-shield-tab-${k}`}
                  className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
                    tab === k
                      ? "border-foreground text-foreground"
                      : "border-transparent text-foreground/55 hover:text-foreground/80"
                  }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && ov && (
        <div className="space-y-6" data-testid="admin-shield-overview">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Platform fees earned" testId="admin-shield-fees"
                  value={usd(ov.platform_fees_earned_cents)} />
            <Stat label="Platform balance" testId="admin-shield-platform-balance"
                  value={usd(ov.platform_balance?.available)} />
            <Stat label="Businesses" testId="admin-shield-businesses-count"
                  value={`${ov.businesses_approved || 0} / ${ov.businesses_provisioned || 0}`}
                  hint="verified / provisioned" />
            <Stat label="Open exposure" testId="admin-shield-exposure"
                  tone={ov.open_deficit_cents ? "text-red-600" : ""}
                  value={usd(ov.open_deficit_cents)}
                  hint={`${ov.open_deficits || 0} returned deposits`} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(ov.transfers_by_status || {}).map(([s, v]) => (
              <Stat key={s} label={s} testId={`admin-shield-status-${s}`}
                    value={v.count} hint={usd(v.amount_cents)} />
            ))}
          </div>

          <div className="rounded-xl border border-foreground/10 bg-background p-5">
            <div className="text-base font-medium">What this rail cannot do</div>
            <p className="mt-1 text-sm text-foreground/60">
              Verified against Shield's published API. Anything here must be covered by
              another provider or a change on Shield's side.
            </p>
            <ul className="mt-3 space-y-2" data-testid="admin-shield-gaps">
              {(ov.capability_gaps || []).map((g) => (
                <li key={g} className="flex items-start gap-2 text-sm text-foreground/70">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  {g}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap gap-2">
              {(ov.rails_supported || []).map((r) => (
                <span key={r} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
                  {r} supported
                </span>
              ))}
            </div>
          </div>

          {ov.commercials && (
            <div className="rounded-xl border border-foreground/10 bg-background p-5"
                 data-testid="admin-shield-commercials">
              <div className="text-base font-medium">Commercials</div>
              <p className="mt-1 text-sm text-foreground/60">
                Agreed with Shield. Verqo enforces the caps before the API call so a user
                sees a clear message instead of a bank rejection.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <Stat label="Minimum platform fee" testId="admin-shield-min-fee"
                      value={`${ov.commercials.platform_fee_minimum_pct}%`}
                      hint="between two businesses · bank top-ups and payouts have none" />
                <Stat label="Wire cap" testId="admin-shield-wire-cap"
                      value={usd(ov.commercials.wire_max_cents)} hint="per transfer" />
                <Stat label="Default daily limit" testId="admin-shield-daily-limit"
                      value={usd(ov.commercials.default_daily_limit_cents)}
                      hint="per business · raise it on the Businesses tab" />
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "businesses" && (
        <div className="overflow-x-auto rounded-xl border border-foreground/10 bg-background">
          <table className="w-full text-sm" data-testid="admin-shield-businesses-table">
            <thead className="text-left text-xs uppercase tracking-wide text-foreground/50">
              <tr className="border-b border-foreground/10">
                <th className="px-4 py-3">Company</th><th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Business id</th><th className="px-4 py-3">Verification</th>
                <th className="px-4 py-3">Available</th><th className="px-4 py-3">Escrow</th>
                <th className="px-4 py-3">Owed back</th><th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {businesses.map((b) => (
                <tr key={b.business_id} className="border-b border-foreground/5"
                    data-testid={`admin-shield-biz-row-${b.business_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{b.company || "—"}</div>
                    <div className="text-xs text-foreground/50">{b.email}</div>
                  </td>
                  <td className="px-4 py-3 text-foreground/60">{b.role}</td>
                  <td className="px-4 py-3 font-mono text-xs text-foreground/55">{b.business_id}</td>
                  <td className="px-4 py-3">{b.onboarding_status}</td>
                  <td className="px-4 py-3 tabular-nums">{usd(b.balance?.available)}</td>
                  <td className="px-4 py-3 tabular-nums text-sky-700">{usd(b.balance?.held)}</td>
                  <td className={`px-4 py-3 tabular-nums ${b.balance?.deficit ? "text-red-600" : ""}`}>
                    {usd(b.balance?.deficit)}
                  </td>
                  <td className="px-4 py-3">
                    {b.onboarding_status !== "approved" && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline"
                                onClick={() => trigger("businesses", b.business_id, "approve")}
                                data-testid={`admin-shield-approve-${b.business_id}`}>
                          Approve
                        </Button>
                        <Button size="sm" variant="outline"
                                onClick={() => trigger("businesses", b.business_id, "reject")}
                                data-testid={`admin-shield-reject-${b.business_id}`}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {businesses.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-foreground/50">
              No businesses provisioned yet.
            </div>
          )}
        </div>
      )}

      {tab === "accounts" && (
        <div className="space-y-5" data-testid="admin-shield-accounts">
          {accounts?.platform && !accounts.platform.account && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4"
                 data-testid="admin-shield-platform-unverified">
              <div className="text-sm text-amber-800">
                <div className="font-medium">Verqo's own account is not open yet.</div>
                <div className="mt-0.5 text-amber-800/80">
                  The platform business exists at Shield but still needs business
                  verification before it gets an account and routing number.
                </div>
              </div>
              <Button variant="outline" onClick={verifyPlatform}
                      data-testid="admin-shield-verify-platform-btn">
                Verify Verqo's business
              </Button>
            </div>
          )}

          {accounts?.platform?.account && (
            <div className="rounded-xl border border-foreground/10 bg-background p-5"
                 data-testid="admin-shield-platform-account">
              <div className="text-base font-medium">Verqo's own account</div>
              <div className="mt-3 grid gap-4 sm:grid-cols-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-foreground/50">Title</div>
                  <div className="mt-1">{accounts.platform.account.account_title}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-foreground/50">Account</div>
                  <div className="mt-1 font-mono">{accounts.platform.account.account_number}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-foreground/50">Routing</div>
                  <div className="mt-1 font-mono">{accounts.platform.account.routing_number}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-foreground/50">Bank</div>
                  <div className="mt-1">{accounts.platform.account.bank_name}</div>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-foreground/10 bg-background">
            <table className="w-full text-sm" data-testid="admin-shield-accounts-table">
              <thead className="text-left text-xs uppercase tracking-wide text-foreground/50">
                <tr className="border-b border-foreground/10">
                  <th className="px-4 py-3">Business</th><th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Account title</th>
                  <th className="px-4 py-3">Account number</th>
                  <th className="px-4 py-3">Routing</th><th className="px-4 py-3">Held</th>
                  <th className="px-4 py-3">Status</th><th className="px-4 py-3">Simulate inbound</th>
                </tr>
              </thead>
              <tbody>
                {(accounts?.accounts || []).map((row) => (
                  <tr key={row.account.id} className="border-b border-foreground/5"
                      data-testid={`admin-shield-account-row-${row.account.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{row.company || "—"}</div>
                      <div className="text-xs text-foreground/50">{row.email}</div>
                    </td>
                    <td className="px-4 py-3 text-foreground/60">{row.role}</td>
                    <td className="px-4 py-3">{row.account.account_title}</td>
                    <td className="px-4 py-3 font-mono text-xs">{row.account.account_number}</td>
                    <td className="px-4 py-3 font-mono text-xs">{row.account.routing_number}</td>
                    <td className="px-4 py-3 text-foreground/60">
                      {row.account.ownership === "fbo" ? "FBO · Cross River" : "direct"}
                    </td>
                    <td className="px-4 py-3">{row.account.status}</td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="outline" onClick={() => receive(row.account.id)}
                              data-testid={`admin-shield-receive-${row.account.id}`}>
                        Receive $250
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(accounts?.accounts || []).length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-foreground/50">
                No accounts open yet. Shield opens one the moment a business is approved.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "persons" && (
        <div className="overflow-x-auto rounded-xl border border-foreground/10 bg-background">
          <table className="w-full text-sm" data-testid="admin-shield-persons-table">
            <thead className="text-left text-xs uppercase tracking-wide text-foreground/50">
              <tr className="border-b border-foreground/10">
                <th className="px-4 py-3">Person</th><th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Business</th><th className="px-4 py-3">Checks</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Recertify by</th>
                <th className="px-4 py-3">Simulate</th>
              </tr>
            </thead>
            <tbody>
              {(persons?.persons || []).map((p) => (
                <tr key={p.id} className="border-b border-foreground/5"
                    data-testid={`admin-shield-person-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.first_name} {p.last_name}</div>
                    <div className="text-xs text-foreground/50">SSN ••••{p.ssn_last4}</div>
                  </td>
                  <td className="px-4 py-3 text-foreground/60">
                    {p.role === "control_person" ? "control" : `${p.ownership_percentage}% owner`}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-foreground/55">{p.business}</td>
                  <td className="px-4 py-3 text-xs text-foreground/60">
                    {Object.entries(p.verification_checks || {})
                      .map(([k, v]) => `${k}: ${v}`).join(" · ")}
                  </td>
                  <td className={`px-4 py-3 font-medium ${
                    p.verification_status === "verified" ? "text-emerald-700"
                      : p.verification_status === "rejected" ? "text-red-600" : "text-amber-600"
                  }`}>
                    {p.verification_status}
                  </td>
                  <td className="px-4 py-3 text-foreground/60">
                    {p.recertification_due_at ? p.recertification_due_at.slice(0, 10) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {["pending", "review"].includes(p.verification_status) && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline"
                                onClick={() => trigger("persons", p.id, "verify")}
                                data-testid={`admin-shield-person-verify-${p.id}`}>
                          Verify
                        </Button>
                        <Button size="sm" variant="outline"
                                onClick={() => trigger("persons", p.id, "reject")}
                                data-testid={`admin-shield-person-reject-${p.id}`}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(persons?.persons || []).length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-foreground/50">
              No owners or control persons submitted yet.
            </div>
          )}
        </div>
      )}

      {tab === "limits" && (
        <div className="space-y-4" data-testid="admin-shield-limits">
          {(limits?.requests || []).length === 0 && (
            <div className="rounded-xl border border-foreground/10 bg-background px-4 py-10 text-center text-sm text-foreground/50">
              No limit requests yet.
            </div>
          )}
          {(limits?.requests || []).map((r) => {
            const ctx = r.context || {};
            const bank = ctx.connected_bank;
            const act = ctx.verqo_activity || {};
            const ver = ctx.verification || {};
            const pending = r.status === "pending";
            return (
              <div key={r.id} className="rounded-xl border border-foreground/10 bg-background p-5"
                   data-testid={`admin-limit-request-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-base font-medium">
                      {r.company || r.email}
                      <span className="ml-2 text-xs text-foreground/50">{r.role}</span>
                    </div>
                    <div className="mt-1 text-sm text-foreground/60">
                      {usd(r.current_daily_limit_cents)} → <span className="font-medium text-foreground">
                        {usd(r.requested_daily_limit_cents)}
                      </span> a day
                      {r.expected_monthly_volume_cents
                        ? ` · expects ${usd(r.expected_monthly_volume_cents)}/mo`
                        : ""}
                    </div>
                    <p className="mt-2 max-w-2xl text-sm text-foreground/70">"{r.reason}"</p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs font-medium ${
                    pending ? "border-amber-200 bg-amber-50 text-amber-700"
                      : r.status === "approved" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-red-200 bg-red-50 text-red-600"
                  }`}>
                    {r.status}
                    {r.status === "approved" && r.granted_daily_limit_cents
                      ? ` · ${usd(r.granted_daily_limit_cents)}`
                      : ""}
                  </span>
                </div>

                <div className="mt-4 grid gap-4 border-t border-foreground/10 pt-4 text-sm sm:grid-cols-4">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-foreground/50">Connected bank</div>
                    {bank ? (
                      <div className="mt-1">
                        <div>{bank.institution || "Linked"}</div>
                        {bank.verified_by_plaid === false && (
                          <div className="text-xs text-amber-700">not bank-verified</div>
                        )}
                        {(bank.accounts || []).slice(0, 2).map((a, i) => (
                          <div key={i} className="text-xs text-foreground/55">
                            {a.name} ••{a.mask} ·{" "}
                            {a.available != null ? `$${Number(a.available).toLocaleString()}` : "—"}
                          </div>
                        ))}
                        {bank.cashflow?.avg_monthly_inflow != null && (
                          <div className="mt-1 text-xs text-foreground/55">
                            Avg inflow ${Number(bank.cashflow.avg_monthly_inflow).toLocaleString()}/mo
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-1 text-amber-700">No bank connected</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-foreground/50">Verqo activity</div>
                    <div className="mt-1 text-foreground/70">
                      {act.orders || 0} orders · {act.invoices || 0} invoices
                    </div>
                    <div className="text-xs text-foreground/55">
                      sent today {usd(act.sent_today_cents || 0)}
                      {act.returned_count ? ` · ${act.returned_count} returned` : " · no returns"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-foreground/50">Verification</div>
                    <div className="mt-1 text-foreground/70">{ver.onboarding_status || "unknown"}</div>
                    <div className="text-xs text-foreground/55">
                      {(ver.persons || []).length} people ·{" "}
                      {ver.all_persons_verified ? "all verified" : "checks outstanding"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-foreground/50">Decision</div>
                    {pending ? (
                      <div className="mt-1 flex flex-col gap-2">
                        <Button size="sm" onClick={() => decideLimit(r, "approve")}
                                data-testid={`admin-limit-approve-${r.id}`}>
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => decideLimit(r, "decline")}
                                data-testid={`admin-limit-decline-${r.id}`}>
                          Decline
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-1 text-xs text-foreground/55">
                        {r.decided_by} · {(r.decided_at || "").slice(0, 10)}
                        {r.needs_shield_signoff && (
                          <div className="mt-1 text-amber-700">Needs partner-bank sign-off too</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "fees" && (
        <div className="space-y-5" data-testid="admin-shield-fees">
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Swept into Verqo's account" testId="admin-fees-swept"
                  value={usd(fees?.summary?.swept_cents || 0)}
                  hint="banking fees that have actually landed" />
            <Stat label="Outstanding" testId="admin-fees-outstanding"
                  value={usd(fees?.summary?.outstanding_cents || 0)}
                  hint="charged but not yet swept — retry below" />
            <Stat label="Stuck at the partner bank" testId="admin-fees-stuck"
                  value={usd(fees?.summary?.stuck_at_shield_cents || 0)}
                  hint="their platform balance · no API to move it yet" />
          </div>

          <div className="overflow-x-auto rounded-xl border border-foreground/10 bg-background">
            <table className="w-full text-sm" data-testid="admin-fees-table">
              <thead className="text-left text-xs uppercase tracking-wide text-foreground/50">
                <tr className="border-b border-foreground/10">
                  <th className="px-4 py-3">Member</th><th className="px-4 py-3">Fee</th>
                  <th className="px-4 py-3">Kind</th><th className="px-4 py-3">Bank's cut</th>
                  <th className="px-4 py-3">Status</th><th className="px-4 py-3">When</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {(fees?.sweeps || []).map((s) => (
                  <tr key={s.id} className="border-b border-foreground/5"
                      data-testid={`admin-fee-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div>{s.member?.company || s.member?.email || s.user_id}</div>
                      <div className="text-xs text-foreground/50">{s.memo}</div>
                    </td>
                    <td className="px-4 py-3 font-medium">{usd(s.fee_cents)}</td>
                    <td className="px-4 py-3 text-foreground/60">{s.kind}</td>
                    <td className="px-4 py-3 text-foreground/60">
                      {s.shield_fee_cents != null ? usd(s.shield_fee_cents) : "—"}
                    </td>
                    <td className={`px-4 py-3 font-medium ${
                      s.status === "swept" ? "text-emerald-700"
                        : s.status === "failed" ? "text-red-600" : "text-amber-600"
                    }`}>
                      {s.status}
                      {s.failure ? <span className="block text-xs font-normal text-foreground/50">{s.failure}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground/55">
                      {(s.created_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-4 py-3">
                      {s.status !== "swept" && s.status !== "superseded" && (
                        <Button size="sm" variant="outline" onClick={() => retryFee(s.id)}
                                data-testid={`admin-fee-retry-${s.id}`}>
                          Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(fees?.sweeps || []).length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-foreground/50">
                No banking fees charged yet.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "treasury" && (
        <div className="space-y-5" data-testid="admin-shield-treasury">
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Available to withdraw" testId="admin-treasury-available"
                  value={usd(treasury?.available_cents || 0)}
                  hint="Verqo's own balance at Cross River" />
            <Stat label="Owed back" testId="admin-treasury-deficit"
                  tone={treasury?.deficit_cents ? "text-red-600" : ""}
                  value={usd(treasury?.deficit_cents || 0)}
                  hint="blocks outgoing money until it clears" />
            <Stat label="Stuck at the partner bank" testId="admin-treasury-stuck"
                  value={usd(fees?.summary?.stuck_at_shield_cents ?? ov?.platform_balance?.available ?? 0)}
                  hint="their platform balance · no API to move it yet" />
          </div>

          <div className="rounded-xl border border-foreground/10 bg-background p-5">
            <div className="text-xs uppercase tracking-wide text-foreground/50">
              Verqo's account at the bank
            </div>
            <div className="mt-2 grid gap-4 sm:grid-cols-3 text-sm">
              <div>
                <div className="text-foreground/50 text-xs">Account number</div>
                <div className="font-mono" data-testid="admin-treasury-account-number">
                  {treasury?.account?.account_number || treasury?.account?.account_number_masked || "—"}
                </div>
              </div>
              <div>
                <div className="text-foreground/50 text-xs">Routing number</div>
                <div className="font-mono" data-testid="admin-treasury-routing-number">
                  {treasury?.account?.routing_number || "—"}
                </div>
              </div>
              <div>
                <div className="text-foreground/50 text-xs">Status</div>
                <div>
                  {treasury?.onboarding_status
                    || (treasury?.account?.status === "open" ? "approved · account open" : "—")}
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-start gap-2 text-xs text-foreground/55">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{treasury?.platform_balance_note}</span>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div className="rounded-xl border border-foreground/10 bg-background p-5">
              <div className="text-xs uppercase tracking-wide text-foreground/50">
                Withdraw to Verqo's bank
              </div>
              {treasury?.bank ? (
                <>
                  <div className="mt-2 text-sm" data-testid="admin-treasury-bank">
                    {treasury.bank.bank_name || treasury.bank.nickname}
                    <span className="ml-2 font-mono text-foreground/60">
                      {treasury.bank.account_masked}
                    </span>
                  </div>
                  <div className="mt-4 space-y-3">
                    <input type="number" min="0" step="0.01" placeholder="Amount"
                           value={payoutForm.amount}
                           onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                           className="w-full rounded-lg border border-foreground/15 bg-transparent px-3 py-2 text-sm"
                           data-testid="admin-treasury-amount" />
                    <select value={payoutForm.rail}
                            onChange={(e) => setPayoutForm({ ...payoutForm, rail: e.target.value })}
                            className="w-full rounded-lg border border-foreground/15 bg-transparent px-3 py-2 text-sm"
                            data-testid="admin-treasury-rail">
                      <option value="ach">Standard ACH · 2 business days</option>
                      <option value="same_day_ach">Same-day ACH · today</option>
                      <option value="rtp">Instant · seconds, if the bank takes it</option>
                      <option value="wire">Wire · today, up to $750,000</option>
                    </select>
                    <input placeholder="Memo (optional)" value={payoutForm.memo}
                           onChange={(e) => setPayoutForm({ ...payoutForm, memo: e.target.value })}
                           className="w-full rounded-lg border border-foreground/15 bg-transparent px-3 py-2 text-sm"
                           data-testid="admin-treasury-memo" />
                    <div className="flex gap-2">
                      <Button disabled={busy || !payoutForm.amount}
                              onClick={sendTreasuryPayout}
                              data-testid="admin-treasury-withdraw">
                        Withdraw {payoutForm.amount ? usd(Math.round(Number(payoutForm.amount) * 100)) : ""}
                      </Button>
                      <Button variant="outline" onClick={removeTreasuryBank}
                              data-testid="admin-treasury-remove-bank">
                        Change bank
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="mt-3 space-y-3">
                  <div className="text-sm text-foreground/60">
                    Add the traditional bank Verqo withdraws to.
                  </div>
                  <input placeholder="Routing number" value={bankForm.routing_number}
                         onChange={(e) => setBankForm({ ...bankForm, routing_number: e.target.value })}
                         className="w-full rounded-lg border border-foreground/15 bg-transparent px-3 py-2 text-sm font-mono"
                         data-testid="admin-treasury-bank-routing" />
                  <input placeholder="Account number" value={bankForm.account_number}
                         onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                         className="w-full rounded-lg border border-foreground/15 bg-transparent px-3 py-2 text-sm font-mono"
                         data-testid="admin-treasury-bank-account" />
                  <input placeholder="Bank name" value={bankForm.bank_name}
                         onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                         className="w-full rounded-lg border border-foreground/15 bg-transparent px-3 py-2 text-sm"
                         data-testid="admin-treasury-bank-name" />
                  <Button disabled={busy || !bankForm.routing_number || !bankForm.account_number}
                          onClick={saveTreasuryBank}
                          data-testid="admin-treasury-save-bank">
                    Save bank
                  </Button>
                </div>
              )}
            </div>

            <div className="overflow-x-auto rounded-xl border border-foreground/10 bg-background">
              <table className="w-full text-sm" data-testid="admin-treasury-withdrawals">
                <thead className="text-left text-xs uppercase tracking-wide text-foreground/50">
                  <tr className="border-b border-foreground/10">
                    <th className="px-4 py-3">When</th><th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Rail</th><th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(treasury?.withdrawals || []).map((w) => (
                    <tr key={w.id} className="border-b border-foreground/5"
                        data-testid={`admin-treasury-withdrawal-${w.id}`}>
                      <td className="px-4 py-3 text-xs text-foreground/55">
                        {(w.created_at || "").slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-4 py-3 tabular-nums">{usd(w.amount_cents)}</td>
                      <td className="px-4 py-3 text-foreground/60">{w.rail}</td>
                      <td className={`px-4 py-3 font-medium ${
                        w.status === "succeeded" ? "text-emerald-700"
                          : w.status === "failed" || w.status === "returned" ? "text-red-600"
                            : "text-amber-600"}`}>
                        {w.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(treasury?.withdrawals || []).length === 0 && (
                <div className="px-4 py-10 text-center text-sm text-foreground/50">
                  No withdrawals yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "transfers" && (
        <div className="overflow-x-auto rounded-xl border border-foreground/10 bg-background">
          <table className="w-full text-sm" data-testid="admin-shield-transfers-table">
            <thead className="text-left text-xs uppercase tracking-wide text-foreground/50">
              <tr className="border-b border-foreground/10">
                <th className="px-4 py-3">Transfer</th><th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Fee</th><th className="px-4 py-3">Rail</th>
                <th className="px-4 py-3">Escrow</th><th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Simulate</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((t) => (
                <tr key={t.id} className="border-b border-foreground/5"
                    data-testid={`admin-shield-transfer-${t.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-foreground/55">{t.id}</td>
                  <td className="px-4 py-3 tabular-nums">{usd(t.amount_cents)}</td>
                  <td className="px-4 py-3 tabular-nums text-foreground/60">
                    {t.platform_fee_cents ? usd(t.platform_fee_cents) : "—"}
                  </td>
                  <td className="px-4 py-3 text-foreground/60">{t.rail || "internal"}</td>
                  <td className="px-4 py-3 text-xs text-sky-700">
                    {t.hold_id ? `${t.hold_until?.type || "hold"} · ${t.hold_status}` : "—"}
                  </td>
                  <td className="px-4 py-3 font-medium">{t.status}</td>
                  <td className="px-4 py-3">
                    {t.status === "processing" && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline"
                                onClick={() => trigger("transfers", t.id, "succeed")}
                                data-testid={`admin-shield-succeed-${t.id}`}>Succeed</Button>
                        <Button size="sm" variant="outline"
                                onClick={() => trigger("transfers", t.id, "fail")}
                                data-testid={`admin-shield-fail-${t.id}`}>Fail</Button>
                      </div>
                    )}
                    {t.status === "succeeded" && t.rail && (
                      <Button size="sm" variant="outline"
                              onClick={() => trigger("transfers", t.id, "return")}
                              data-testid={`admin-shield-return-${t.id}`}>Return</Button>
                    )}
                    {t.hold_id && t.hold_status === "held"
                      && ["delivered", "tracking_number"].includes(t.hold_until?.type) && (
                      <Button size="sm" variant="outline"
                              onClick={() => trigger("holds", t.hold_id, "deliver")}
                              data-testid={`admin-shield-deliver-${t.hold_id}`}>Mark delivered</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {transfers.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-foreground/50">No transfers yet.</div>
          )}
        </div>
      )}

      {tab === "deficits" && deficits && (
        <div className="space-y-4" data-testid="admin-shield-deficits">
          <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
            <div className="text-sm text-red-800">
              <div className="font-medium">
                {usd(deficits.open_cents)} open across {deficits.open_count || 0} returned deposits
              </div>
              <div className="mt-0.5 text-red-700/80">{deficits.liability_note}</div>
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-foreground/10 bg-background">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-foreground/50">
                <tr className="border-b border-foreground/10">
                  <th className="px-4 py-3">When</th><th className="px-4 py-3">Transfer</th>
                  <th className="px-4 py-3">Business</th><th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Reason</th><th className="px-4 py-3">State</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {(deficits.deficits || []).map((d) => (
                  <tr key={d.transfer_id} className="border-b border-foreground/5"
                      data-testid={`admin-shield-deficit-${d.transfer_id}`}>
                    <td className="px-4 py-3 text-xs text-foreground/55">{(d.at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3 font-mono text-xs">{d.transfer_id}</td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground/55">{d.business}</td>
                    <td className="px-4 py-3 tabular-nums text-red-600">{usd(d.amount_cents)}</td>
                    <td className="px-4 py-3 text-foreground/60">{d.failure_code || "returned"}</td>
                    <td className="px-4 py-3">
                      {d.resolved
                        ? <span className="inline-flex items-center gap-1 text-emerald-600">
                            <CheckCircle2 className="h-4 w-4" /> resolved</span>
                        : <span className="inline-flex items-center gap-1 text-red-600">
                            <Ban className="h-4 w-4" /> open</span>}
                    </td>
                    <td className="px-4 py-3">
                      {!d.resolved && (
                        <div className="flex gap-2">
                          <Button size="sm"
                                  onClick={() => coverDeficit(d.transfer_id)}
                                  data-testid={`admin-shield-cover-${d.transfer_id}`}>
                            Cover from Verqo
                          </Button>
                          <Button size="sm" variant="outline"
                                  onClick={() => resolveDeficit(d.transfer_id)}
                                  data-testid={`admin-shield-resolve-${d.transfer_id}`}>
                            Mark repaid
                          </Button>
                        </div>
                      )}
                      {d.fee_reversal?.review_needed && (
                        <div className="mt-1 text-xs text-amber-600">
                          Check whether a Verqo fee was charged on this deposit
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(deficits.deficits || []).length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-foreground/50">
                No returned deposits. Nothing at risk.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "events" && events && (
        <div className="space-y-4" data-testid="admin-shield-events">
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Events received" value={events.count || 0} testId="admin-shield-events-count" />
            <Stat label="Rejected deliveries" value={events.rejected_count || 0}
                  tone={events.rejected_count ? "text-red-600" : ""}
                  testId="admin-shield-rejects-count" hint="Failed signature checks" />
            <Stat label="Handled" value={(events.events || []).filter((e) => e.handled).length}
                  testId="admin-shield-handled-count" />
          </div>
          <div className="overflow-x-auto rounded-xl border border-foreground/10 bg-background">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-foreground/50">
                <tr className="border-b border-foreground/10">
                  <th className="px-4 py-3">Received</th><th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Object</th><th className="px-4 py-3">Handled</th>
                  <th className="px-4 py-3"><Webhook className="h-4 w-4" /></th>
                </tr>
              </thead>
              <tbody>
                {(events.events || []).map((e) => (
                  <tr key={e.id} className="border-b border-foreground/5"
                      data-testid={`admin-shield-event-${e.id}`}>
                    <td className="px-4 py-3 text-xs text-foreground/55">
                      {(e.received_at || "").slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="px-4 py-3 font-medium">{e.type}</td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground/55">{e.object_id}</td>
                    <td className="px-4 py-3">{e.handled ? "yes" : "no"}</td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="outline"
                              onClick={async () => {
                                try {
                                  await api.post(`/admin/shield/events/${e.id}/resend`);
                                  toast.success("Shield will redeliver this event.");
                                } catch (err) { errorToastFromAxios(err, "Resend failed."); }
                              }}
                              data-testid={`admin-shield-resend-${e.id}`}>Resend</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(events.events || []).length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-foreground/50">
                <Users className="mx-auto mb-2 h-5 w-5 opacity-40" />
                No webhook events received yet.
              </div>
            )}
          </div>
        </div>
      )}
      {tab === "drift" && (
        <div className="space-y-5" data-testid="admin-shield-drift">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            <div className="flex gap-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-medium">Read-only comparison. Nothing is changed by opening this.</div>
                <div className="text-sky-800/80">
                  Verqo&apos;s own wallet figure against Shield&apos;s spendable balance, per account.
                  Where they disagree, Shield is the one holding the money.
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={downloadDrift} disabled={busy}
                    data-testid="admin-drift-csv-btn">
              <Download className="mr-2 h-4 w-4" /> CSV
            </Button>
          </div>

          {!drift ? (
            <div className="px-4 py-10 text-center text-sm text-foreground/50">Measuring…</div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <Stat label="Net drift" testId="admin-drift-net"
                      value={usd(drift.summary.net_drift_cents)}
                      tone={drift.summary.net_drift_cents ? "text-red-600" : "text-emerald-700"}
                      hint="Verqo total minus Shield total" />
                <Stat label="Accounts disagreeing" testId="admin-drift-disagreeing"
                      value={(drift.summary.by_classification.verqo_ahead || 0)
                             + (drift.summary.by_classification.shield_ahead || 0)}
                      hint={`of ${drift.summary.users_examined} examined`} />
                <Stat label="Explained by test money" testId="admin-drift-test-money"
                      value={usd(drift.summary.explained_by_test_money_cents)}
                      hint="Test-mode top-ups that were never real" />
                <Stat label="Not mirrored at all" testId="admin-drift-unmirrored"
                      value={usd(drift.summary.unmirrored_cents)}
                      tone={drift.summary.unmirrored_cents ? "text-red-600" : ""}
                      hint={`${drift.summary.unmirrored_accounts} funded accounts with no Shield business`} />
                <Stat label="Funded but not approved" testId="admin-drift-blocked"
                      value={drift.summary.blocked_but_funded}
                      tone={drift.summary.blocked_but_funded ? "text-amber-600" : ""}
                      hint="Shows a balance, cannot legally move it" />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <Stat label="Verqo shows" testId="admin-drift-verqo-total"
                      value={usd(drift.summary.verqo_balance_total_cents)} />
                <Stat label="Shield holds (available)" testId="admin-drift-shield-total"
                      value={usd(drift.summary.shield_available_total_cents)}
                      hint={`+ ${usd(drift.summary.shield_pending_total_cents)} pending · `
                            + `${usd(drift.summary.shield_held_total_cents)} in escrow`} />
                <Stat label="Shield deficits" testId="admin-drift-deficit-total"
                      value={usd(drift.summary.shield_deficit_total_cents)}
                      tone={drift.summary.shield_deficit_total_cents ? "text-red-600" : ""}
                      hint="Verqo is liable for these" />
              </div>

              <div className="flex flex-wrap gap-2" data-testid="admin-drift-legend">
                {Object.entries(drift.summary.by_classification).map(([k, n]) => {
                  const [label, tone, why] = DRIFT_LABEL[k] || [k, "", ""];
                  return (
                    <span key={k} title={why}
                          className="rounded-full border border-foreground/10 px-3 py-1 text-xs"
                          data-testid={`admin-drift-count-${k}`}>
                      <span className={tone}>{label}</span>
                      <span className="ml-2 tabular-nums text-foreground/50">{n}</span>
                    </span>
                  );
                })}
              </div>

              <div className="overflow-x-auto rounded-xl border border-foreground/10 bg-background">
                <table className="w-full text-sm" data-testid="admin-drift-table">
                  <thead className="text-left text-xs uppercase tracking-wide text-foreground/50">
                    <tr className="border-b border-foreground/10">
                      <th className="px-4 py-3">Account</th>
                      <th className="px-4 py-3">KYB</th>
                      <th className="px-4 py-3 text-right">Verqo shows</th>
                      <th className="px-4 py-3 text-right">Shield holds</th>
                      <th className="px-4 py-3 text-right">Drift</th>
                      <th className="px-4 py-3">Reading</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drift.rows
                      .filter((r) => r.drift_cents !== 0 || r.blocked_but_funded)
                      .slice(0, 200)
                      .map((r) => {
                      const [label, tone] = DRIFT_LABEL[r.classification] || [r.classification, ""];
                      return (
                        <tr key={r.verqo_user_id} className="border-b border-foreground/5"
                            data-testid={`admin-drift-row-${r.verqo_user_id}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium">{r.email}</div>
                            <div className="text-xs text-foreground/50">
                              {r.role}{r.company ? ` · ${r.company}` : ""}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <span className={r.onboarding_status === "approved"
                              ? "text-emerald-700" : "text-foreground/55"}>
                              {r.onboarding_status || "not started"}
                            </span>
                            {r.blocked_but_funded && (
                              <AlertTriangle className="ml-1 inline h-3 w-3 text-amber-600" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {usd(r.verqo_balance_cents)}
                            {r.verqo_test_money_cents ? (
                              <div className="text-xs text-foreground/45">
                                incl. {usd(r.verqo_test_money_cents)} test
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {r.shield ? usd(r.shield.available)
                              : r.classification === "shield_unreachable" ? "—"
                              : <span className="text-foreground/40">no account</span>}
                            {r.shield && (r.shield.held || r.shield.pending) ? (
                              <div className="text-xs text-foreground/45">
                                {r.shield.held ? `${usd(r.shield.held)} escrow` : ""}
                                {r.shield.held && r.shield.pending ? " · " : ""}
                                {r.shield.pending ? `${usd(r.shield.pending)} pending` : ""}
                              </div>
                            ) : null}
                          </td>
                          <td className={`px-4 py-3 text-right tabular-nums font-medium ${
                            r.drift_cents > 0 ? "text-red-600"
                              : r.drift_cents < 0 ? "text-amber-600" : "text-foreground/40"}`}>
                            {r.classification === "shield_unreachable" ? "—" : usd(r.drift_cents)}
                          </td>
                          <td className={`px-4 py-3 text-xs ${tone}`}>{label}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {drift.rows.filter((r) => r.drift_cents !== 0 || r.blocked_but_funded).length === 0 && (
                  <div className="px-4 py-10 text-center text-sm text-emerald-700">
                    <CheckCircle2 className="mx-auto mb-2 h-5 w-5" />
                    Every account agrees with Shield to the cent.
                  </div>
                )}
              </div>

              <p className="text-xs text-foreground/45">
                Showing the accounts that disagree or are funded without approval.
                {" "}{drift.rows.filter((r) => r.drift_cents === 0 && !r.blocked_but_funded).length}
                {" "}of {drift.summary.users_examined} accounts agree and are hidden.
                Measured {(drift.summary.generated_at || "").slice(0, 19).replace("T", " ")} UTC.
                Verqo&apos;s wallet is maintained by its own code today, so a gap here is expected —
                this is the baseline to migrate against, not a bug report.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
